import {
  type ReportPane,
  reportPanes,
} from "@/profitability/interactive-report/report-panes";
import type { ReportOutput } from "@/profitability/types";

export const paneTab = (paneId: string) => `pane/${paneId}`;

export const paneOf = (tab: string) =>
  tab.startsWith("pane/") ? tab.slice("pane/".length) : null;

const FIXED = new Set([
  "dashboard",
  "review",
  "workbook",
  "documents",
  "insights",
]);

/** Bookmarks from when this tab was called Trends. */
const SECTION_ALIAS: Record<string, string> = { trends: "insights" };

export interface TemplateTab {
  value: string;
  label: string;
  count?: number;
}

/**
 * Until the period's output is in (or for a period built before reports
 * declared datasets) the Dashboard stays: it is where an empty or building
 * template explains itself.
 */
export const hasReview = (
  output: ReportOutput | undefined,
  work: string | null,
) =>
  !output ||
  !!work ||
  output.datasets.some((dataset) =>
    dataset.columns.some((column) => column.flag),
  );

/** A declared view keeps its blocks and controls; Review delegates to the app. */
export const templatePanes = (
  output: ReportOutput | undefined,
  review: boolean,
): ReportPane[] =>
  output
    ? reportPanes(output).filter((pane) => !review || pane.surface !== "review")
    : [];

/**
 * Periods and Settings are not tabs: the picker opens one, the page menu the
 * other. Dashboard, Review, Insights and the workbook are always there, each
 * saying why it is empty until it has something to show.
 */
export const templateTabs = ({
  panes,
  documents = false,
}: {
  panes: ReportPane[];
  documents?: boolean;
}): TemplateTab[] => [
  { value: "dashboard", label: "Dashboard" },
  { value: "review", label: "Review" },
  { value: "insights", label: "Insights" },
  { value: "workbook", label: "Workbook" },
  ...panes.map((pane) => ({
    value: paneTab(pane.id),
    label: pane.title,
    count: pane.count,
  })),
  ...(documents ? [{ value: "documents", label: "Documents" }] : []),
];

export const tabOf = (section: string | undefined, pane?: string) => {
  if (pane) return paneTab(pane);
  if (!section) return section;
  return SECTION_ALIAS[section] ?? section;
};

/**
 * The tab the URL names, when the page has it; else where the template
 * opens: its Dashboard when it has a Review, else its first pane (else
 * record tab), so a report with nothing to review opens on what its script
 * shows rather than on an empty Review.
 */
export const currentTab = ({
  section,
  pane,
  tabs,
  review,
  waiting = false,
}: {
  section: string | undefined;
  pane: string | undefined;
  tabs: TemplateTab[];
  /** Whether the period has a Review to open on (`hasReview`). */
  review: boolean;
  waiting?: boolean;
}) => {
  const named = tabOf(section, pane);
  if (named && tabs.some((tab) => tab.value === named)) return named;
  // A deep link to the documents holds while the period that shows the tab
  // is still on the way.
  if (waiting && named === "documents") return named;
  if (review) return "dashboard";
  return (
    tabs.find((tab) => paneOf(tab.value))?.value ??
    tabs.find((tab) => !FIXED.has(tab.value))?.value ??
    "dashboard"
  );
};

/**
 * The tabs of a template's period, as its page shows them and the nav column
 * lists them: Review carries the count of what is still open.
 */
export const templatePageTabs = ({
  output,
  work,
  documents,
  activeCount,
}: {
  output: ReportOutput | undefined;
  work: string | null;
  documents: boolean;
  activeCount: number;
}) => {
  const review = hasReview(output, work);
  const panes = templatePanes(output, review);
  const tabs = templateTabs({ panes, documents }).map((item) =>
    item.value === "review"
      ? { ...item, count: activeCount || undefined }
      : item,
  );
  return { review, panes, tabs };
};
